/**
 * Platform identity chip — coloured square with the platform initial or icon.
 * Used everywhere a platform needs a visual identity.
 */

import { cn } from '../../../lib/utils';
import type { PlatformId } from '../../../config/platforms';
import { PLATFORM_CONFIGS } from '../../../config/platforms';

interface PlatformBadgeProps {
  platform: PlatformId;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function PlatformBadge({ platform, size = 'md', className }: Readonly<PlatformBadgeProps>) {
  const cfg = PLATFORM_CONFIGS[platform];
  const initials = PLATFORM_INITIALS[platform];

  const sizeClass = {
    sm: 'w-6 h-6 text-[10px]',
    md: 'w-8 h-8 text-xs',
    lg: 'w-10 h-10 text-sm',
  }[size];

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-bold shrink-0',
        cfg.bgClass,
        cfg.textClass,
        sizeClass,
        // Instagram needs explicit bg because it's a gradient (not a plain bg class)
        platform === 'instagram' && 'bg-none',
        className
      )}
      style={platform === 'instagram' ? { background: 'linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)' } : undefined}
      aria-label={cfg.name}
      title={cfg.name}
    >
      {initials}
    </span>
  );
}

const PLATFORM_INITIALS: Record<PlatformId, string> = {
  facebook: 'f',
  instagram: 'ig',
  linkedin: 'in',
  youtube: '▶',
  x: 'X',
  threads: '@',
  tiktok: '♪',
  pinterest: 'P',
};
